import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';

import { Button } from './button';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value: string | number;
  onChange: (value: string) => void;
  name?: string;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  editable?: boolean;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'disabled' | 'name' | 'onChange' | 'value'>;
}

export const Select = ({
  options,
  value,
  onChange,
  name,
  ariaLabel,
  className = '',
  disabled = false,
  editable = false,
  inputProps,
}: SelectProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const selectedValue = String(value);
  const matchedIndex = options.findIndex((option) => option.value === selectedValue);
  const selectedIndex = matchedIndex >= 0 ? matchedIndex : editable ? -1 : 0;
  const [activeIndex, setActiveIndex] = useState(Math.max(selectedIndex, 0));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const enabledIndexes = useMemo(
    () => options.map((option, index) => ({ option, index })).filter(({ option }) => !option.disabled),
    [options],
  );

  useEffect(() => {
    if (selectedIndex >= 0) {
      setActiveIndex(selectedIndex);
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const close = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const move = (direction: 1 | -1): void => {
    const position = enabledIndexes.findIndex(({ index }) => index === activeIndex);
    const next = enabledIndexes[(position + direction + enabledIndexes.length) % enabledIndexes.length];
    if (next) {
      setActiveIndex(next.index);
    }
  };

  const choose = (option: SelectOption): void => {
    if (option.disabled) {
      return;
    }
    onChange(option.value);
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      move(event.key === 'ArrowDown' ? 1 : -1);
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open && options[activeIndex]) {
        choose(options[activeIndex]);
      } else {
        setOpen(true);
      }
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleEditableKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      move(event.key === 'ArrowDown' ? 1 : -1);
    }
    if (event.key === 'Enter' && open) {
      if (selectedIndex >= 0 && options[activeIndex]) {
        event.preventDefault();
        choose(options[activeIndex]);
      } else {
        setOpen(false);
      }
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div
      className={`select-control ${open ? 'open' : ''} ${disabled ? 'disabled' : ''} ${className}`.trim()}
      aria-disabled={disabled}
      ref={rootRef}
    >
      {name && <input type="hidden" name={name} value={selectedValue} />}
      {editable ? (
        <div
          className="select-trigger select-editable-trigger"
          onPointerDown={(event) => {
            if (disabled) {
              return;
            }
            if (event.target !== inputRef.current) {
              event.preventDefault();
              inputRef.current?.focus();
              setOpen(true);
            }
          }}
        >
          <input
            {...inputProps}
            ref={inputRef}
            role="combobox"
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            autoComplete="off"
            disabled={disabled}
            value={selectedValue}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              onChange(event.currentTarget.value);
              setOpen(true);
            }}
            onKeyDown={handleEditableKeyDown}
          />
          <ChevronDown aria-hidden="true" />
        </div>
      ) : (
        <Button
          variant="plain"
          className="select-trigger"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={handleKeyDown}
        >
          <span>{selected?.label ?? ''}</span>
          <ChevronDown />
        </Button>
      )}
      <div className="select-menu" role="listbox" aria-hidden={!open}>
        {options.map((option, index) => {
          const isSelected = option.value === selectedValue;
          return (
            <Button
              variant="plain"
              className={`select-option ${isSelected ? 'selected' : ''} ${index === activeIndex ? 'active' : ''}`.trim()}
              role="option"
              aria-selected={isSelected}
              disabled={option.disabled}
              key={option.value}
              onPointerEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
            >
              <span>{option.label}</span>
              {isSelected && <Check />}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
